<template>
  <div class="dashboard-stats">
    <!-- Вкладки -->
    <BaseTabs
      v-model="activeTab"
      :tabs="statsTabs"
      @tab-click="handleStatsTabClick"
      @middle-click="openStatsTabInNewWindow"
    />

    <!-- Фильтры периода/группировки -->
    <div class="stats-filters card">
      <div class="filter-group filter-group--group-by">
        <label class="filter-label">Группировка</label>
        <div class="filter-tabs">
          <button
            v-for="option in groupByOptions"
            :key="option.value"
            class="filter-tab"
            :class="{ active: filters.group_by === option.value }"
            @click="setGroupBy(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <div class="filter-group filter-group--period">
        <label class="filter-label">Период</label>
        <div class="period-row">
          <BaseDateRangePicker
            v-model="dateRange"
            placeholder="Выберите период"
            :presets="datePresets"
            :max-date="today"
            @change="onDateRangeChange"
          />
          <div class="period-presets">
            <button
              v-for="preset in quickDatePresets"
              :key="preset.key"
              type="button"
              class="period-preset-badge"
              :class="{ active: activeQuickPreset === preset.key }"
              @click="applyQuickPreset(preset)"
            >
              {{ preset.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <FilterBlock
      v-if="activeTab === 'orders'"
      :search-fields="ordersSearchFields"
      :auto-search="true"
      :show-inactive-checkbox="false"
      toggle-label="Фильтры"
      @filter-change="onOrdersFilterChange"
    />

    <FilterBlock
      v-if="activeTab === 'vehicle'"
      :search-fields="vehicleSearchFields"
      :auto-search="true"
      :show-inactive-checkbox="false"
      toggle-label="Фильтры"
      @filter-change="onVehicleFilterChange"
    />

    <FilterBlock
      v-if="activeTab === 'expense'"
      :search-fields="expensesSearchFields"
      :auto-search="true"
      :show-inactive-checkbox="false"
      toggle-label="Фильтры"
      @filter-change="onExpensesFilterChange"
    />

    <!-- Загрузка -->
    <div v-if="store.loading" class="stats-state card">
      <span class="stats-loader"></span>
      Загрузка статистики...
    </div>

    <!-- Ошибка -->
    <div v-else-if="store.error" class="stats-state stats-error card">
      {{ store.error }}
    </div>

    <template v-else-if="store.periods.length">
      <DashboardInvoiceTab
        v-if="activeTab === 'invoice'"
        :periods="store.periods"
      />
      <DashboardOrdersTab
        v-if="activeTab === 'orders'"
        :periods="store.periods"
      />
      <DashboardExpenseTab
        v-if="activeTab === 'expense'"
        :periods="store.periods"
      />
      <DashboardVehicleTab
        v-if="activeTab === 'vehicle'"
        :periods="store.periods"
      />
    </template>

    <div v-else class="stats-state card no-data">
      Нет данных за выбранный период
    </div>
  </div>
</template>

<script>
import { useDashboardStore } from "@/stores/dashboardStore";

import DashboardInvoiceTab from "@/components/Dashboard/DashboardInvoiceTab.vue";
import DashboardOrdersTab from "@/components/Dashboard/DashboardOrdersTab.vue";
import DashboardExpenseTab from "@/components/Dashboard/DashboardExpenseTab.vue";
import DashboardVehicleTab from "@/components/Dashboard/DashboardVehicleTab.vue";
import FilterBlock from "@/components/Filter/FilterBlock.vue";

import ordersSearchFields from "@/config/filters/dashboard/orders.json";
import vehicleSearchFields from "@/config/filters/dashboard/vehicles.json";
import expensesSearchFields from "@/config/filters/dashboard/expenses.json";

import { getQueryParam } from "@/utils/urlUtils";

const TAB_METRIC_MAP = {
  invoice: "invoices",
  orders: "orders",
  expense: "expenses",
  vehicle: "vehicles",
};

const VALID_GROUP_BY = ["day", "week", "month"];
const VALID_TABS = ["orders", "invoice", "expense", "vehicle"];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default {
  name: "DashboardStats",

  components: {
    DashboardInvoiceTab,
    DashboardOrdersTab,
    DashboardExpenseTab,
    DashboardVehicleTab,
    FilterBlock,
  },

  props: {
    companyId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    const now = new Date();
    const currentMonthFrom = this.formatDate(
      new Date(now.getFullYear(), now.getMonth(), 1)
    );
    const currentMonthTo = this.formatDate(now);

    const tabFromUrl = getQueryParam("tab", this.$route);
    const initialTab = VALID_TABS.includes(tabFromUrl)
      ? tabFromUrl
      : "orders";

    const groupByFromUrl = getQueryParam("group_by", this.$route);
    const initialGroupBy = VALID_GROUP_BY.includes(groupByFromUrl)
      ? groupByFromUrl
      : "week";

    const fromFromUrl = getQueryParam("from", this.$route);
    const toFromUrl = getQueryParam("to", this.$route);
    const hasValidDates =
      DATE_RE.test(fromFromUrl) && DATE_RE.test(toFromUrl);

    const initialFrom = hasValidDates
      ? fromFromUrl
      : currentMonthFrom;
    const initialTo = hasValidDates ? toFromUrl : currentMonthTo;

    return {
      store: useDashboardStore(),

      activeTab: initialTab,
      activeQuickPreset: "current-month",

      statsTabs: [
        {
          key: "orders",
          label: "По статусам",
          icon: "📋",
        },
        {
          key: "invoice",
          label: "По счетам",
          icon: "💰",
        },
        {
          key: "expense",
          label: "По расходам",
          icon: "💸",
        },
        {
          key: "vehicle",
          label: "По транспорту",
          icon: "🚚",
        },
      ],

      filters: {
        group_by: initialGroupBy,
        from: initialFrom,
        to: initialTo,
      },

      dateRange: {
        from: initialFrom,
        to: initialTo,
      },

      ordersExtraFilters: {},
      vehicleExtraFilters: {},
      expensesExtraFilters: {},

      groupByOptions: [
        { value: "day", label: "День" },
        { value: "week", label: "Неделя" },
        { value: "month", label: "Месяц" },
      ],

      ordersSearchFields,
      vehicleSearchFields,
      expensesSearchFields,
    };
  },

  computed: {
    today() {
      return this.formatDate(new Date());
    },

    quickDatePresets() {
      return [
        {
          key: "current-month",
          label: "Этот месяц",
          getRange: ({ today }) => {
            const now = new Date();
            return {
              from: this.formatDate(
                new Date(now.getFullYear(), now.getMonth(), 1)
              ),
              to: today,
            };
          },
        },
        {
          key: "previous-month",
          label: "Прошлый месяц",
          getRange: () => {
            const now = new Date();
            return {
              from: this.formatDate(
                new Date(now.getFullYear(), now.getMonth() - 1, 1)
              ),
              to: this.formatDate(
                new Date(now.getFullYear(), now.getMonth(), 0)
              ),
            };
          },
        },
        {
          key: "current-quarter",
          label: "Этот квартал",
          getRange: ({ today }) => {
            const now = new Date();
            const qStart = Math.floor(now.getMonth() / 3) * 3;
            return {
              from: this.formatDate(
                new Date(now.getFullYear(), qStart, 1)
              ),
              to: today,
            };
          },
        },
        {
          key: "current-year",
          label: "Этот год",
          getRange: ({ today }) => ({
            from: this.formatDate(
              new Date(new Date().getFullYear(), 0, 1)
            ),
            to: today,
          }),
        },
      ];
    },

    datePresets() {
      return [
        {
          key: "today",
          label: "Сегодня",
          getRange: ({ today }) => ({ from: today, to: today }),
        },
        {
          key: "yesterday",
          label: "Вчера",
          getRange: () => {
            const d = new Date();
            d.setDate(d.getDate() - 1);
            const v = this.formatDate(d);
            return { from: v, to: v };
          },
        },
        {
          key: "current-week",
          label: "Эта неделя",
          getRange: ({ today }) => {
            const now = new Date();
            const dow = now.getDay() === 0 ? 7 : now.getDay();
            const mon = new Date(now);
            mon.setDate(now.getDate() - (dow - 1));
            return { from: this.formatDate(mon), to: today };
          },
        },
        {
          key: "previous-week",
          label: "Прошлая неделя",
          getRange: () => {
            const now = new Date();
            const dow = now.getDay() === 0 ? 7 : now.getDay();
            const mon = new Date(now);
            mon.setDate(now.getDate() - (dow - 1));
            const prevMon = new Date(mon);
            prevMon.setDate(mon.getDate() - 7);
            const prevSun = new Date(mon);
            prevSun.setDate(mon.getDate() - 1);
            return {
              from: this.formatDate(prevMon),
              to: this.formatDate(prevSun),
            };
          },
        },
        {
          key: "current-month",
          label: "Этот месяц",
          getRange: ({ today }) => {
            const now = new Date();
            return {
              from: this.formatDate(
                new Date(now.getFullYear(), now.getMonth(), 1)
              ),
              to: today,
            };
          },
        },
        {
          key: "previous-month",
          label: "Прошлый месяц",
          getRange: () => {
            const now = new Date();
            return {
              from: this.formatDate(
                new Date(now.getFullYear(), now.getMonth() - 1, 1)
              ),
              to: this.formatDate(
                new Date(now.getFullYear(), now.getMonth(), 0)
              ),
            };
          },
        },
        {
          key: "current-quarter",
          label: "Этот квартал",
          getRange: ({ today }) => {
            const now = new Date();
            const qStart = Math.floor(now.getMonth() / 3) * 3;
            return {
              from: this.formatDate(
                new Date(now.getFullYear(), qStart, 1)
              ),
              to: today,
            };
          },
        },
        {
          key: "previous-quarter",
          label: "Прошлый квартал",
          getRange: () => {
            const now = new Date();
            const curQ = Math.floor(now.getMonth() / 3);
            const prevQStart = (curQ - 1 + 4) % 4;
            const prevQYear =
              curQ === 0 ? now.getFullYear() - 1 : now.getFullYear();
            const from = new Date(prevQYear, prevQStart * 3, 1);
            const to = new Date(prevQYear, prevQStart * 3 + 3, 0);
            return {
              from: this.formatDate(from),
              to: this.formatDate(to),
            };
          },
        },
        {
          key: "current-year",
          label: "Этот год",
          getRange: ({ today }) => ({
            from: this.formatDate(
              new Date(new Date().getFullYear(), 0, 1)
            ),
            to: today,
          }),
        },
        {
          key: "previous-year",
          label: "Прошлый год",
          getRange: () => {
            const y = new Date().getFullYear() - 1;
            return {
              from: this.formatDate(new Date(y, 0, 1)),
              to: this.formatDate(new Date(y, 11, 31)),
            };
          },
        },
      ];
    },
  },

  methods: {
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    syncFiltersToUrl() {
      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          tab: this.activeTab,
          group_by: this.filters.group_by,
          from: this.filters.from,
          to: this.filters.to,
        },
      });
    },

    // Пытается сопоставить текущий диапазон дат с одним из quickDatePresets
    resolveActivePreset(from, to) {
      const matched = this.quickDatePresets.find((preset) => {
        const range = preset.getRange({ today: this.today });
        return range.from === from && range.to === to;
      });
      return matched ? matched.key : null;
    },

    setGroupBy(value) {
      this.filters.group_by = value;
      this.syncFiltersToUrl();
      this.loadStats();
    },

    handleStatsTabClick({ key }) {
      if (key === this.activeTab) return;

      this.switchTab(key);
    },

    openStatsTabInNewWindow({ key }) {
      const route = this.$router.resolve({
        name: this.$route.name,
        params: this.$route.params,
        query: {
          ...this.$route.query,
          tab: key,
        },
      });

      window.open(route.href, "_blank", "noopener");
    },

    switchTab(tab) {
      this.activeTab = tab;
      if (tab !== "orders") {
        this.ordersExtraFilters = {};
      }
      if (tab !== "vehicle") {
        this.vehicleExtraFilters = {};
      }
      if (tab !== "expense") {
        this.expensesExtraFilters = {};
      }
      this.syncFiltersToUrl();
      this.loadStats();
    },

    applyQuickPreset(preset) {
      const range = preset.getRange({ today: this.today });
      this.activeQuickPreset = preset.key;
      this.dateRange = { from: range.from, to: range.to };
      this.filters.from = range.from;
      this.filters.to = range.to;
      this.syncFiltersToUrl();
      this.loadStats();
    },

    onDateRangeChange(range) {
      this.dateRange = { from: range.from, to: range.to };
      this.filters.from = range.from;
      this.filters.to = range.to;
      this.activeQuickPreset = this.resolveActivePreset(
        range.from,
        range.to
      );
      this.syncFiltersToUrl();
      this.loadStats();
    },

    onOrdersFilterChange({ searchValues }) {
      const extra = {};
      for (const [key, value] of Object.entries(searchValues)) {
        if (value !== null && value !== undefined && value !== "") {
          extra[key] = value;
        }
      }
      this.ordersExtraFilters = extra;
      this.loadStats();
    },

    onVehicleFilterChange({ searchValues }) {
      const extra = {};
      for (const [key, value] of Object.entries(searchValues)) {
        if (value !== null && value !== undefined && value !== "") {
          extra[key] = value;
        }
      }
      this.vehicleExtraFilters = extra;
      this.loadStats();
    },

    onExpensesFilterChange({ searchValues }) {
      const extra = {};
      for (const [key, value] of Object.entries(searchValues)) {
        if (value !== null && value !== undefined && value !== "") {
          extra[key] = value;
        }
      }
      this.expensesExtraFilters = extra;
      this.loadStats();
    },

    loadStats() {
      let extraParams = {};

      if (this.activeTab === "orders") {
        for (const [key, value] of Object.entries(
          this.ordersExtraFilters
        )) {
          extraParams[`filters[${key}]`] = value;
        }
      } else if (this.activeTab === "vehicle") {
        for (const [key, value] of Object.entries(
          this.vehicleExtraFilters
        )) {
          extraParams[`filters[${key}]`] = value;
        }
      } else if (this.activeTab === "expense") {
        for (const [key, value] of Object.entries(
          this.expensesExtraFilters
        )) {
          extraParams[`filters[${key}]`] = value;
        }
      }

      this.store.fetchPeriodStats(this.companyId, {
        ...this.filters,
        ...extraParams,
        metric: TAB_METRIC_MAP[this.activeTab],
      });
    },
  },

  created() {
    this.activeQuickPreset = this.resolveActivePreset(
      this.filters.from,
      this.filters.to
    );
    this.syncFiltersToUrl();
    this.loadStats();
  },
};
</script>

<style scoped>
.dashboard-stats {
  display: flex;
  flex-direction: column;
}

.stats-filters {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
  margin: 15px 0;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group--group-by {
  flex-shrink: 0;
}

.filter-group--period {
  flex: 1;
  min-width: 0;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  background: var(--color-grey);
  padding: 3px;
  border-radius: 8px;
}

.filter-tab {
  padding: 7px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #888;
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  white-space: nowrap;
}

.filter-tab.active {
  background: #fff;
  color: var(--color-dark);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.period-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.period-row > .drp {
  flex-shrink: 0;
  width: 240px;
}

.period-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.period-preset-badge {
  padding: 5px 13px;
  border: 1px solid var(--color-grey);
  border-radius: 20px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  font-family: "Roboto", sans-serif;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

.period-preset-badge:hover {
  background: var(--color-grey);
  color: var(--color-dark);
}

.period-preset-badge.active {
  background: var(--color-green);
  border-color: var(--color-green);
  color: #fff;
}

.period-preset-badge.active:hover {
  background: var(--color-green-hover);
  border-color: var(--color-green-hover);
}

.stats-state {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #aaa;
  font-style: italic;
  padding: 32px var(--base-in-indent);
  justify-content: center;
}

.stats-error {
  color: var(--color-danger);
  font-style: normal;
}

.stats-loader {
  width: 18px;
  height: 18px;
  border: 2px solid var(--color-grey);
  border-top-color: var(--color-green);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media screen and (max-width: 767px) {
  .stats-filters {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group--period {
    flex: unset;
  }

  .filter-tabs {
    width: max-content;
  }

  .period-preset-badge {
    font-size: 14px;
    padding: 8.5px 16px;
  }
}
</style>
